import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import styled from "styled-components";
import moment from "moment";

import {
  Tr,
  Th,
  Thead,
  Td,
  CLink,
  H3,
  Caption
} from "@appcues/component-library";
import { setDataType, formatDate } from "utils";
import { AUTO_PROFILE_ATTRIBUTES } from "constants/profileAttributes";
import { useToggle } from "hooks/useToggle";
import { selectAccountProfileAttributeByName } from "reducers/account/profileAttributes";

const dataTypeOptions = {
  ["Text"]: {
    icon: "fas fa-font",
    label: "Text"
  },
  ["Number"]: {
    icon: "fas fa-calculator",
    label: "Number"
  },
  ["Date"]: {
    icon: "fas fa-calendar-alt",
    label: "Date"
  },
  ["True/False"]: {
    icon: "fas fa-check-square",
    label: "True/False"
  },
  ["Unknown Data Type"]: {
    icon: "fas fa-user",
    label: "Unknown"
  }
};

const Container = styled.div`
  font-family: ${props => props.theme["$font-body"]};
  position: absolute;
  top: ${props => (props.top || 0) + window.scrollY}px;
  left: ${props => props.left || 0}px;
  margin-left: 15px;
  display: flex;
  cursor: default;
  flex-direction: column;
  justify-content: flex-start;
  width: 480px;
  margin-top: 16px;
  padding: 16px 20px;
  border-radius: 4px;
  border: 2px solid ${props => props.theme["$gray-3"]};
  background: white;
  color: ${props => props.theme["$gray-90"]};
  h3,
  p {
    margin: 0;
  }

  .attribute-label {
    font-weight: 400;
    margin: 0 8px 0 0;
  }

  .attribute-name {
    color: ${props => props.theme["$gray-30"]};
    margin-bottom: 0;
    font-size: 14px;
  }

  .attribute-description {
    color: ${props => props.theme["$gray-50"]};
    font-size: 14px;
    margin-bottom: 8px;
    margin-top: -8px;
    line-height: 20px;
  }

  .link {
    margin: 0;
    color: ${props => props.theme["$blue"]};
    font-weight: 300;
    cursor: pointer;

    &.manage-link {
      i {
        padding-left: 4px;
      }
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
`;

const StyledTh = styled(Th)`
  padding: 4px 0;
  color: ${props => props.theme["$gray-50"]};

  &:not(:first-of-type) {
    padding-left: 16px;
  }
`;

const StyledTd = styled(Td)`
  padding: 4px 0;
  word-break: break-all;

  &:not(:first-of-type) {
    padding-left: 16px;
    font-weight: 300;
  }

  &:last-of-type {
    width: 100px;
  }
`;

const FlexRow = styled.div`
  display: flex;
  flex-wrap: ${props => (props.flexWrap ? "wrap" : "nowrap")};
  justify-content: ${props => props.justifyContent || "space-between"};
  align-items: ${props => props.align};
`;

const CardHeader = styled(FlexRow)`
  margin-bottom: 8px;
`;

const DataType = styled.div`
  margin-bottom: 8px;

  .card-label {
    color: ${props => props.theme["$gray-30"]};
    padding-right: 8px;
  }

  i {
    padding-right: 8px;
  }
`;

const Timestamp = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${props => props.theme["$gray-30"]};
  font-size: 14px;
  height: 24px;
  margin: -4px 0 8px 0;

  p {
    padding: 0 8px;
    margin: 0;
    font-weight: 300;
  }
`;

export const ProfileAttributeFlyout = ({
  attribute: { label, name, description, lastSeenTs, values },
  top,
  left
}) => {
  const [showData, toggleShowData] = useToggle(false);

  const lastSeen = moment(lastSeenTs);
  const isStale = lastSeen.diff(Date.now(), "days") < -10;
  const linkTabState = name.match(/^_/) ? "autoProperties" : "customProperties";
  const dataType = setDataType(values);

  const displayLabel = label || name;
  const displayDescription = AUTO_PROFILE_ATTRIBUTES[name]
    ? AUTO_PROFILE_ATTRIBUTES[name].description
    : description;

  return (
    <Container top={top} left={left}>
      <CardHeader align="start">
        <FlexRow
          align="baseline"
          justifyContent="flex-start"
          width="100%"
          flexWrap
        >
          <H3 className="attribute-label">{displayLabel}</H3>
          {displayLabel !== name && (
            <Caption className="attribute-name">({name})</Caption>
          )}
        </FlexRow>
        <FlexRow align="top" justifyContent="flex-end" width="100%">
          <CLink
            className="link manage-link"
            href={`/settings/events?tabState=${linkTabState}`}
          >
            <span>Manage</span>
            <i className="fas fa-long-arrow-alt-right" />
          </CLink>
        </FlexRow>
      </CardHeader>
      {isStale && (
        <Timestamp>
          <i className="fas fa-exclamation-triangle" />
          <p>Last seen {lastSeen.fromNow()}</p>
        </Timestamp>
      )}
      {dataType && (
        <DataType>
          <span className="card-label">Data Type: </span>
          <span className="card-value">
            <i className={dataTypeOptions[dataType].icon} />
            <span>{dataType}</span>
          </span>
        </DataType>
      )}
      {displayDescription && (
        <DataType>
          <span className="card-label">Description: </span>
          <span className="card-value">{displayDescription}</span>
        </DataType>
      )}
      <a
        className="link see-example-data-link"
        onClick={e => {
          e.stopPropagation();
          toggleShowData();
        }}
      >
        {showData ? "Hide" : "Show"} example data...
      </a>
      {showData && (
        <div className="example-data">
          <hr style={{ margin: "1rem 0 0.5rem 0" }} />
          <table>
            <Thead>
              <Tr>
                <StyledTh>Example Values</StyledTh>
              </Tr>
            </Thead>
            <tbody>
              {values &&
                Object.values(values).map((valueName, i) => (
                  <Tr key={i}>
                    <StyledTd>
                      {dataType === "Date" && !isNaN(Number(valueName))
                        ? formatDate(valueName)
                        : String(valueName)}
                    </StyledTd>
                  </Tr>
                ))}

              {!values ||
                (Object.values(values).length === 0 && (
                  <Tr>No Values Found</Tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </Container>
  );
};

ProfileAttributeFlyout.propTypes = {
  top: PropTypes.number,
  left: PropTypes.number,
  attribute: PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    lastSeenTs: PropTypes.number,
    values: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    )
  })
};

const mapStateToProps = (state, { name }) => ({
  attribute: selectAccountProfileAttributeByName(state, name)
});

const ConnectedProfileAttributeFlyout = connect(mapStateToProps)(
  ProfileAttributeFlyout
);

ConnectedProfileAttributeFlyout.propTypes = {
  name: PropTypes.string
};

export default ConnectedProfileAttributeFlyout;
